import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { IconCategory, MapMode } from '@warplan/shared';
import { ICON_CATALOG, getIconEntry, getOrderedCategoriesForMode } from '../lib/icon-catalog.js';
import { IconPalette } from './IconPalette.js';
import { getCategoryStroke } from '../lib/category-colors.js';
import type { MapBackgroundKind } from '../lib/foxhole-maps.js';

export interface PlaceMode {
  category: IconCategory;
  iconId: string;
}

/** Options chosen before/during arrow drawing. */
export interface ArrowDrawOptions {
  kind: IconCategory;
  /** Custom color override; null = use category default. */
  color: string | null;
  /** Show arrowhead at the first vertex. */
  arrowheadStart: boolean;
  /** Show arrowhead at the last vertex. */
  arrowheadEnd: boolean;
  /** Render the stroke as a dashed line. */
  dashed: boolean;
}

/** Live drafting status (number of points placed). */
export interface ArrowDraftStatus {
  pointCount: number;
}

export interface MapToolbarProps {
  mapBackground: MapBackgroundKind;
  onToggleBackground: () => void;
  /** Toggles the "Ink & Frontline" easter-egg background (community mod by Arkhitecton). */
  onToggleInkBackground: () => void;
  showGrid: boolean;
  onToggleGrid: () => void;
  showSubregions: boolean;
  onToggleSubregions: () => void;

  placeMode: PlaceMode | null;
  onChangePlaceMode: (next: PlaceMode | null) => void;
  /** When false, banner buttons are visible but disabled (read-only viewer). */
  canEdit: boolean;
  /** Categories that are off-limits for the current user (buttons disabled). */
  forbiddenCategories?: ReadonlySet<IconCategory> | undefined;
  /** Icon IDs that are off-limits in the fly-out palette (greyed out). */
  forbiddenIconIds?: ReadonlySet<string> | undefined;
  /** Override the absolute position of the toolbar container. */
  style?: CSSProperties;
  /**
   * When set, an extra button is rendered below the background/grid toggles
   * that triggers the war-plan export modal. Only shown when defined
   * (callers gate visibility by role).
   */
  onExportWarPlan?: () => void;
  /**
   * When set, an extra button is rendered next to the war-plan export that
   * triggers the fac-build-msup org export modal.
   */
  onExportFacBuildMsup?: () => void;
  /**
   * When set, an extra button is rendered for the MSup-only export.
   */
  onExportMsup?: () => void;
  /**
   * When set, an extra button is rendered for the operation export modal
   * (only shown in operation mode).
   */
  onExportOperation?: () => void;

  /** Arrow draw integration. */
  arrowOptions: ArrowDrawOptions | null;
  arrowDraft: ArrowDraftStatus | null;
  onSetArrowOptions: (next: ArrowDrawOptions | null) => void;
  onConfirmArrowDraft: () => void;
  onCancelArrowDraft: () => void;
  forbiddenArrowCategories?: ReadonlySet<IconCategory> | undefined;
  /** Active view mode — selects which set of categories the toolbar lists. */
  mode?: MapMode;
}

const buttonBase: CSSProperties = {
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const ARROW_LABELS: Record<IconCategory, string> = {
  combat: 'Flèche combat',
  build: 'Flèche build',
  facility: 'Flèche facility',
  logistic: 'Flèche logistique',
  navy: 'Flèche navale',
  op_info: 'Flèche info',
  op_attack: 'Flèche attaque',
  op_defend: 'Flèche défense',
  op_arty: 'Flèche artillerie',
  op_build: 'Flèche build',
  op_navy: 'Flèche navale',
  op_unit: 'Flèche unité',
  op_vehicle: 'Flèche véhicule',
};

export function MapToolbar({
  mapBackground,
  onToggleBackground,
  onToggleInkBackground,
  showGrid,
  onToggleGrid,
  showSubregions,
  onToggleSubregions,
  placeMode,
  onChangePlaceMode,
  canEdit,
  forbiddenCategories,
  forbiddenIconIds,
  style,
  onExportWarPlan,
  onExportFacBuildMsup,
  onExportMsup,
  onExportOperation,
  arrowOptions,
  arrowDraft,
  onSetArrowOptions,
  onConfirmArrowDraft,
  onCancelArrowDraft,
  forbiddenArrowCategories,
  mode = 'planner',
}: MapToolbarProps): React.JSX.Element {
  const orderedCategories = getOrderedCategoriesForMode(mode);
  const [open, setOpen] = useState<IconCategory | null>(null);
  const [arrowFlyout, setArrowFlyout] = useState<boolean>(false);
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open && !arrowFlyout) return;
    function onDocClick(e: MouseEvent): void {
      const root = toolbarRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        setOpen(null);
        setArrowFlyout(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setOpen(null);
        setArrowFlyout(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, arrowFlyout]);

  function handleBannerClick(category: IconCategory): void {
    if (!canEdit || (forbiddenCategories?.has(category) ?? false)) return;
    if (placeMode?.category === category) {
      onChangePlaceMode(null);
      setOpen(null);
      return;
    }
    if (arrowOptions) onCancelArrowDraft();
    onChangePlaceMode({
      category,
      iconId: ICON_CATALOG[category].defaultIconId,
    });
    setOpen(category);
    setArrowFlyout(false);
  }

  function handlePick(category: IconCategory, iconId: string): void {
    onChangePlaceMode({ category, iconId });
    setOpen(null);
  }

  function handleArrowToggle(): void {
    if (!canEdit) return;
    if (arrowOptions) {
      // Toggle options flyout while drafting.
      setArrowFlyout((prev) => !prev);
      return;
    }
    if (placeMode) onChangePlaceMode(null);
    setArrowFlyout((prev) => !prev);
    setOpen(null);
  }

  function handleArrowPickCategory(category: IconCategory): void {
    if (!canEdit || (forbiddenArrowCategories?.has(category) ?? false)) return;
    onSetArrowOptions({
      kind: category,
      color: null,
      arrowheadStart: false,
      arrowheadEnd: true,
      dashed: false,
    });
    setArrowFlyout(true);
  }

  const arrowDrafting = arrowOptions !== null;

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label="Map tools"
      className="absolute top-2 z-30 flex flex-col gap-2"
      style={{ left: 8, transition: 'left 150ms ease', ...style }}
    >
      {/* Arrows section (top): single toggle button + draft validate/cancel. */}
      <div className="flex flex-col gap-1 rounded border border-slate-700 bg-slate-900/90 p-1 shadow-lg backdrop-blur">
        {(() => {
          const armed = arrowOptions !== null;
          const stroke = armed ? getCategoryStroke(arrowOptions.kind) : '#cbd5e1';
          const disabled = !canEdit;
          return (
            <button
              type="button"
              title={armed && arrowOptions ? `Flèche ${ARROW_LABELS[arrowOptions.kind]}` : 'Tracer une flèche'}
              aria-label="Tracer une flèche"
              aria-pressed={armed}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={handleArrowToggle}
              style={buttonBase}
              className={
                'relative rounded transition ' +
                (disabled
                  ? 'opacity-40 cursor-not-allowed'
                  : armed
                    ? 'bg-emerald-600 ring-2 ring-emerald-300'
                    : 'hover:bg-slate-700')
              }
            >
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <defs>
                  <marker
                    id="tb-head-main"
                    viewBox="0 0 10 10"
                    refX="9"
                    refY="5"
                    markerWidth="5"
                    markerHeight="5"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 1 L 9 5 L 0 9 z" fill={stroke} />
                  </marker>
                </defs>
                {/* Clean two-segment path: bottom-left → bend → top-right */}
                <polyline
                  points="4,20 4,11 20,4"
                  fill="none"
                  stroke={stroke}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  markerEnd="url(#tb-head-main)"
                />
              </svg>
            </button>
          );
        })()}

        {arrowDrafting ? (
          <div className="mt-1 flex flex-col gap-1 border-t border-slate-700 pt-1">
            <div className="px-1 text-center text-[10px] text-slate-300">
              {arrowDraft?.pointCount ?? 0} pts
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={onConfirmArrowDraft}
                disabled={(arrowDraft?.pointCount ?? 0) < 2}
                title="Valider le tracé (Entrée)"
                aria-label="Valider"
                className={
                  'h-7 flex-1 rounded text-sm font-bold ' +
                  ((arrowDraft?.pointCount ?? 0) < 2
                    ? 'cursor-not-allowed bg-slate-700 text-slate-500'
                    : 'bg-emerald-600 text-white hover:bg-emerald-500')
                }
              >
                ✓
              </button>
              <button
                type="button"
                onClick={onCancelArrowDraft}
                title="Annuler (Échap)"
                aria-label="Annuler"
                className="h-7 flex-1 rounded bg-slate-700 text-sm font-bold text-slate-100 hover:bg-slate-600"
              >
                ✕
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Banner buttons */}
      <div className="flex flex-col gap-1 rounded border border-slate-700 bg-slate-900/90 p-1 shadow-lg backdrop-blur">
        {orderedCategories.map((cat) => {
          const meta = ICON_CATALOG[cat];
          const isArmed = placeMode?.category === cat;
          const shownIconId = isArmed ? placeMode.iconId : meta.defaultIconId;
          const entry = getIconEntry(cat, shownIconId);
          const catDisabled = !canEdit || (forbiddenCategories?.has(cat) ?? false);
          return (
            <button
              key={cat}
              type="button"
              title={meta.label}
              aria-label={meta.label}
              aria-pressed={isArmed}
              aria-disabled={catDisabled}
              disabled={catDisabled}
              onClick={() => handleBannerClick(cat)}
              style={buttonBase}
              className={
                'relative rounded transition ' +
                (catDisabled
                  ? 'opacity-40 cursor-not-allowed'
                  : isArmed
                    ? 'bg-emerald-600 ring-2 ring-emerald-300'
                    : 'hover:bg-slate-700')
              }
            >
              <img src={entry.src} alt="" className="h-7 w-7 object-contain" draggable={false} />
            </button>
          );
        })}
      </div>

      {/* Background + grid toggles (visual only) */}
      <div className="flex flex-col gap-1 rounded border border-slate-700 bg-slate-900/90 p-1 shadow-lg backdrop-blur">
        <button
          type="button"
          title={mapBackground === 'default' ? 'Switch to immersive map' : 'Switch to standard map'}
          onClick={onToggleBackground}
          style={buttonBase}
          className={
            'rounded text-slate-100 transition ' +
            (mapBackground === 'imm'
              ? 'bg-emerald-600 ring-2 ring-emerald-300'
              : 'hover:bg-slate-700')
          }
        >
          <span aria-hidden="true">🗺</span>
        </button>
        <button
          type="button"
          title={
            mapBackground === 'ink'
              ? 'Désactiver le fond « Ink & Frontline » (mod par Arkhitecton)'
              : 'Activer le fond « Ink & Frontline » (mod par Arkhitecton)'
          }
          aria-label="Basculer le fond de carte Ink & Frontline"
          aria-pressed={mapBackground === 'ink'}
          onClick={onToggleInkBackground}
          style={buttonBase}
          className={
            'rounded text-slate-100 transition ' +
            (mapBackground === 'ink'
              ? 'bg-emerald-600 ring-2 ring-emerald-300'
              : 'hover:bg-slate-700')
          }
        >
          <span aria-hidden="true">🖋</span>
        </button>
        <button
          type="button"
          title={
            showSubregions
              ? 'Masquer les sous-régions de contrôle'
              : 'Afficher les sous-régions de contrôle'
          }
          aria-label="Basculer les sous-régions de contrôle"
          aria-pressed={showSubregions}
          onClick={onToggleSubregions}
          style={buttonBase}
          className={
            'rounded text-slate-100 transition ' +
            (showSubregions
              ? 'bg-emerald-600 ring-2 ring-emerald-300'
              : 'opacity-40 hover:bg-slate-700')
          }
        >
          {/* Disc divided into 3 coloured sectors (Colonial / Warden / Neutral)
              illustrating exactly what the layer renders: faction-coloured
              control zones inside a hex region. */}
          <svg
            aria-hidden="true"
            viewBox="-12 -12 24 24"
            width="22"
            height="22"
            stroke="#0b0d12"
            strokeWidth="1.25"
            strokeLinejoin="round"
          >
            <circle cx="0" cy="0" r="10" fill="#3a3f4a" />
            <path d="M0 0 L0 -10 A10 10 0 0 1 8.66 5 Z" fill="#516C4B" />
            <path d="M0 0 L8.66 5 A10 10 0 0 1 -8.66 5 Z" fill="#245682" />
          </svg>
        </button>
        <button
          type="button"
          title={showGrid ? 'Hide grid' : 'Show grid'}
          onClick={onToggleGrid}
          style={buttonBase}
          className={
            'rounded text-slate-100 transition ' +
            (showGrid
              ? 'bg-emerald-600 ring-2 ring-emerald-300'
              : 'opacity-40 hover:bg-slate-700')
          }
        >
          <span aria-hidden="true">⊞</span>
        </button>        {onExportWarPlan && (
          <button
            type="button"
            title="Exporter le war plan (image)"
            aria-label="Exporter le war plan"
            onClick={onExportWarPlan}
            style={buttonBase}
            className="rounded text-slate-100 transition hover:bg-slate-700"
          >
            <span aria-hidden="true">📷</span>
          </button>
        )}
        {onExportFacBuildMsup && (
          <button
            type="button"
            title="Exporter le fac-build org (image)"
            aria-label="Exporter le fac-build org"
            onClick={onExportFacBuildMsup}
            style={buttonBase}
            className="rounded text-slate-100 transition hover:bg-slate-700"
          >
            <span aria-hidden="true">🏭</span>
          </button>
        )}
        {onExportMsup && (
          <button
            type="button"
            title="Exporter le MSup org (image)"
            aria-label="Exporter le MSup org"
            onClick={onExportMsup}
            style={buttonBase}
            className="rounded text-slate-100 transition hover:bg-slate-700"
          >
            <span aria-hidden="true">🔧</span>
          </button>
        )}
        {onExportOperation && (
          <button
            type="button"
            title="Exporter l'opération (image)"
            aria-label="Exporter l'opération"
            onClick={onExportOperation}
            style={buttonBase}
            className="rounded text-orange-200 transition hover:bg-orange-700/40"
          >
            <span aria-hidden="true">📷</span>
          </button>
        )}
      </div>

      {/* Fly-out: positioned right of the banner column */}
      {open && (
        <div
          className="absolute top-0"
          style={{ left: 52 }}
          role="dialog"
          aria-label={`${ICON_CATALOG[open].label} icons`}
        >
          <IconPalette
            category={open}
            selectedIconId={placeMode?.category === open ? placeMode.iconId : undefined}
            onPick={(id) => handlePick(open, id)}
            variant="submenu"
            forbiddenIconIds={forbiddenIconIds}
          />
        </div>
      )}

      {/* Arrow fly-out: category picker (when no kind armed) OR options panel. */}
      {arrowFlyout && !arrowOptions ? (
        <div
          className="absolute z-40 w-56 rounded border border-slate-700 bg-slate-900/95 p-3 shadow-2xl backdrop-blur"
          style={{ left: 52, top: 0 }}
          role="dialog"
          aria-label="Choisir la catégorie de flèche"
        >
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-300">
            Catégorie
          </div>
          <div className="grid grid-cols-1 gap-1">
            {orderedCategories.map((cat) => {
              const disabled = forbiddenArrowCategories?.has(cat) ?? false;
              const stroke = getCategoryStroke(cat);
              return (
                <button
                  key={`pick-${cat}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleArrowPickCategory(cat)}
                  className={
                    'flex items-center gap-2 rounded border border-slate-700 px-2 py-1 text-left text-xs ' +
                    (disabled
                      ? 'cursor-not-allowed opacity-40'
                      : 'hover:border-slate-500 hover:bg-slate-800')
                  }
                >
                  <span
                    aria-hidden="true"
                    style={{ background: stroke }}
                    className="h-3 w-6 rounded"
                  />
                  <span className="text-slate-100">{ARROW_LABELS[cat]}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {arrowFlyout && arrowOptions ? (
        <div
          className="absolute z-40 w-64 rounded border border-slate-700 bg-slate-900/95 p-3 shadow-2xl backdrop-blur"
          style={{ left: 52, top: 0 }}
          role="dialog"
          aria-label="Options de flèche"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
              Options
            </span>
            <button
              type="button"
              onClick={() => setArrowFlyout(false)}
              className="rounded px-1.5 py-0.5 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>

          <div className="mb-2">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">
              Catégorie
            </div>
            <div className="flex flex-wrap gap-1">
              {orderedCategories.map((cat) => {
                const disabled = forbiddenArrowCategories?.has(cat) ?? false;
                const isActive = arrowOptions.kind === cat;
                const stroke = getCategoryStroke(cat);
                return (
                  <button
                    key={`opt-${cat}`}
                    type="button"
                    disabled={disabled}
                    aria-pressed={isActive}
                    onClick={() => onSetArrowOptions({ ...arrowOptions, kind: cat })}
                    title={ARROW_LABELS[cat]}
                    className={
                      'h-7 flex-1 rounded border text-[10px] font-semibold uppercase transition ' +
                      (disabled
                        ? 'cursor-not-allowed border-slate-700 opacity-40'
                        : isActive
                          ? 'border-emerald-400 ring-1 ring-emerald-300'
                          : 'border-slate-700 hover:border-slate-500')
                    }
                    style={{
                      background: isActive ? stroke : 'transparent',
                      color: isActive ? '#fff' : stroke,
                    }}
                  >
                    {ICON_CATALOG[cat].label.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-2">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">Couleur</div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={arrowOptions.color ?? getCategoryStroke(arrowOptions.kind)}
                onChange={(e) =>
                  onSetArrowOptions({ ...arrowOptions, color: e.target.value })
                }
                className="h-7 w-12 cursor-pointer rounded border border-slate-700 bg-slate-800"
              />
              <button
                type="button"
                onClick={() => onSetArrowOptions({ ...arrowOptions, color: null })}
                disabled={arrowOptions.color === null}
                className={
                  'rounded border border-slate-700 px-2 py-1 text-[10px] text-slate-200 ' +
                  (arrowOptions.color === null
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-slate-700')
                }
              >
                Reset
              </button>
            </div>
          </div>

          <div className="mb-2">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">
              Pointes
            </div>
            <div className="flex gap-1">
              {(
                [
                  { key: 'none', label: '—', start: false, end: false },
                  { key: 'end', label: '→', start: false, end: true },
                  { key: 'start', label: '←', start: true, end: false },
                  { key: 'both', label: '↔', start: true, end: true },
                ] as const
              ).map((opt) => {
                const isActive =
                  arrowOptions.arrowheadStart === opt.start &&
                  arrowOptions.arrowheadEnd === opt.end;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() =>
                      onSetArrowOptions({
                        ...arrowOptions,
                        arrowheadStart: opt.start,
                        arrowheadEnd: opt.end,
                      })
                    }
                    title={opt.label}
                    className={
                      'h-7 flex-1 rounded border text-sm font-semibold transition ' +
                      (isActive
                        ? 'border-emerald-400 bg-emerald-600 text-white'
                        : 'border-slate-700 text-slate-200 hover:bg-slate-700')
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-2">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">
              Style
            </div>
            <div className="flex gap-1">
              {(
                [
                  { key: 'solid', label: 'Plein', dashed: false },
                  { key: 'dashed', label: 'Tirets', dashed: true },
                ] as const
              ).map((opt) => {
                const isActive = arrowOptions.dashed === opt.dashed;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => onSetArrowOptions({ ...arrowOptions, dashed: opt.dashed })}
                    className={
                      'flex h-7 flex-1 items-center justify-center gap-1 rounded border text-[10px] font-semibold uppercase transition ' +
                      (isActive
                        ? 'border-emerald-400 bg-emerald-600 text-white'
                        : 'border-slate-700 text-slate-200 hover:bg-slate-700')
                    }
                  >
                    <svg width="28" height="8" viewBox="0 0 28 8" aria-hidden="true">
                      <line
                        x1="2"
                        y1="4"
                        x2="26"
                        y2="4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray={opt.dashed ? '6 5' : undefined}
                      />
                    </svg>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-[10px] text-slate-400">
            Cliquez sur la carte pour placer des points, puis « ✓ ».
          </div>
        </div>
      ) : null}
    </div>
  );
}
